import React, { useState } from 'react';

import { Route, useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import {
  useDeleteRoleMutation,
  useGetRolesQuery,
} from '@school_ify/schoolify-repository/src/teacher/types';

import StyledTooltip from 'components/StyledTooltip/StyledTooltip';
import { ReactComponent as TrashIcon } from 'assets/icons/trashIcon.svg';
import { ReactComponent as PencilIcon } from 'assets/icons/pencil.svg';
import Loader from 'clean/components/common/Loader/Loader';

import RoleDetails from '../RoleDetails/RoleDetails';
import RoleBadge from '../RoleBadge/RoleBadge';

import './RoleTable.scss';

const RoleTable: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const [idsProcessing, setIdsProcessing] = useState([]);
  const [deleteRole] = useDeleteRoleMutation();

  const {
    data: items,
    loading: itemsLoading,
    refetch,
  } = useGetRolesQuery({ fetchPolicy: 'network-only' });

  const onRemove = async (id: string) => {
    setIdsProcessing([...idsProcessing, id]);
    try {
      await deleteRole({
        variables: {
          deleteRolesByPkId: id,
        },
      });
    } catch (err) {
      console.warn(err);
    } finally {
      await refetch();
      setIdsProcessing(idsProcessing.filter(item => item !== id));
    }
  };

  return (
    <div className="roleTable">
      <Route path={['/permissions/roles/new', '/permissions/roles/:id']}>
        <RoleDetails
          onClose={() => {
            history.push('/permissions/roles');
          }}
          onSave={() => {
            refetch();
          }}
        />
      </Route>

      {!items && itemsLoading && (
        <div className="loadingContainer">
          <Loader fade={false} />
        </div>
      )}

      {items?.roles.map(role => {
        return (
          <div
            className={classNames('row', { rowProcessing: idsProcessing.includes(role.id) })}
            key={role.id}
          >
            <div className="iconCell">
              <RoleBadge color={role.colour} />
            </div>
            <div className="labelCell">
              <div className="label">{role.title}</div>
              <div className="members">{t('permissions:RoleTableMembers', { count: 28 })}</div>
            </div>
            <div className="spacerCell"></div>
            <div className="actionsCell">
              <StyledTooltip
                tagName="div"
                direction="up"
                content={<p>{t('permissions:RoleTableEditGroup')}</p>}
              >
                <div
                  className="actionIconContainer actionIconContainerInfo"
                  onClick={() => {
                    history.push(`/permissions/roles/${role.id}`);
                  }}
                >
                  <PencilIcon />
                </div>
              </StyledTooltip>
              <StyledTooltip
                tagName="div"
                direction="up"
                content={<p>{t('permissions:RoleTableDeleteGroup')}</p>}
              >
                <div
                  className="actionIconContainer actionIconContainerDanger"
                  onClick={() => onRemove(role.id)}
                >
                  <TrashIcon />
                </div>
              </StyledTooltip>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default RoleTable;
